/**
 * GeoJSON ↔ Leaflet coordinate helpers.
 * GeoJSON stores [lng, lat]; Leaflet expects [lat, lng].
 */

export type GeoCoordinate = [number, number];

/** Single [lng, lat] → [lat, lng]. */
export const toLatLng = ([lng, lat]: GeoCoordinate): [number, number] => [lat, lng];

/** Linear ring of [lng, lat] → [lat, lng][]. */
export const ringToLatLng = (ring: GeoCoordinate[]): [number, number][] => ring.map(toLatLng);

/** Polygon (array of rings) → [lat, lng][][]. */
export const polygonToLatLng = (poly: GeoCoordinate[][]): [number, number][][] =>
	poly.map(ringToLatLng);

/** MultiPolygon (array of polygons) → [lat, lng][][][]. */
export const multiPolygonToLatLng = (mpoly: GeoCoordinate[][][]): [number, number][][][] =>
	mpoly.map(polygonToLatLng);

/**
 * Convert a polygon or multipolygon coordinates to Leaflet position format.
 * - Polygon outer ring: [lat, lng][]
 * - MultiPolygon: [lat, lng][][][] (natively accepted by Leaflet's Polygon)
 */
export function geoJsonToLeafletPositions(
	coords: GeoCoordinate[][] | GeoCoordinate[][][]
): [number, number][] | [number, number][][][] {
	if (!coords || coords.length === 0) {
		return [];
	}
	if (Array.isArray(coords[0]?.[0]?.[0])) {
		return multiPolygonToLatLng(coords as GeoCoordinate[][][]);
	}
	const ring = (coords as GeoCoordinate[][])[0] ?? [];
	return ring.map(([lng, lat]) => [lat, lng] as [number, number]);
}

export function territoryToLeafletPositions(
	geom:
		| { type: "Polygon"; coordinates: GeoCoordinate[][] }
		| { type: "MultiPolygon"; coordinates: GeoCoordinate[][][] }
): [number, number][] | [number, number][][][] {
	if (geom.type === "MultiPolygon") {
		return multiPolygonToLatLng(geom.coordinates);
	}
	return geoJsonToLeafletPositions(geom.coordinates);
}

export type GeoJsonGeometry =
	| { type: "Point"; coordinates: GeoCoordinate }
	| { type: "MultiPoint"; coordinates: GeoCoordinate[] }
	| { type: "Polygon"; coordinates: GeoCoordinate[][] }
	| { type: "MultiPolygon"; coordinates: GeoCoordinate[][][] };

/** Extract every [lat, lng] from any supported geometry (for bounds fitting). */
export function geometryToLatLngs(g: GeoJsonGeometry): [number, number][] {
	if (g.type === "Point") {
		return [toLatLng(g.coordinates)];
	}
	if (g.type === "MultiPoint") {
		return g.coordinates.map(toLatLng);
	}
	if (g.type === "Polygon") {
		return ringToLatLng(g.coordinates[0] ?? []);
	}
	// Append per-vertex (not `acc.push(...ring)`) so large multipolygons cannot
	// overflow the call stack via spread.
	const acc: [number, number][] = [];
	for (const poly of g.coordinates) {
		for (const point of ringToLatLng(poly[0] ?? [])) {
			acc.push(point);
		}
	}
	return acc;
}
